<template>
  <Teleport to="body">
    <ModalComponent v-model="showUploadFilesModal">
      <template #header>Déposer une pièce justificative</template>
      <template #content>
        <WrapperDropdown
          v-if="!fichier"
          v-model="natureSelectionnée"
          class="wrapperNatures"
          :items="dropDownItems"
          label="Nature des fichiers"
        />
        <BasicText
          class="natureLabel"
          size="h4"
          :label="fichier?.nature"
        />
        <BasicButton
          label="Choisir des fichiers"
          variant="outlined"
          type="secondary"
          class="upload-item"
          @click="selectFilesStart"
        />
        <input
          ref="fileInput"
          type="file"
          :accept="acceptedFileTypesString"
          :multiple="true"
          :hidden="true"
          @change="selectFilesEnd"
        />
        <div
          class="fileNames"
          v-for="(file, index) in filesList"
          :key="`${file.name}_${index}`"
        >
          <BasicText
            size="body-m"
            color="neutral-500"
            class="download"
            :title="file.name"
          >
            {{ file.name }}
          </BasicText>
          <BasicTooltip label="Supprimer le fichier">
            <BasicIcon
              name="close"
              class="icon"
              @click="manager.removeFile(file)"
            />
          </BasicTooltip>
        </div>
        <BasicAlert
          v-if="messageErreur.length"
          :alert-label="messageErreur"
        />
      </template>
      <template #actions>
        <div class="flex flex-gap-4">
          <BasicButton
            label="Annuler"
            type="secondary"
            class="flex-1"
            @click="showUploadFilesModal = false"
          />
          <BasicButton
            label="Importer"
            class="flex-1"
            @click="uploadFiles"
            :disabled="importButtonIsDisabled"
          />
        </div>
      </template>
    </ModalComponent>
  </Teleport>
</template>

<script setup lang="ts">
  import { ref, computed, onBeforeMount, watch } from 'vue'
  import { ACCEPTED_FILE_TYPES } from '../../demande/shared/components/upload/constants'
  import { useDialog } from '@/features/interface/dialog'
  import ModalComponent from '../../modal/ModalComponent.vue'
  import type { Endpoint } from './UploadFiles.types'
  import { useUploadFiles } from './useUploadFiles'
  import type { Dispositif } from '@/features/domaine/types/opco/shared/Dispositif'
  import type { PieceDTO } from '@/features/api/extranet/dto/shared'

  const props = defineProps<{
    code: string
    endpoint: Endpoint
    fichier?: PieceDTO
    dispositif: Dispositif | Dispositif[]
  }>()

  const manager = useUploadFiles(props.endpoint)
  watch(props, () => manager.setState(props), { immediate: true })
  onBeforeMount(async () => await manager.inititialize())

  const { dropDownItems, filesList, messageErreur, natureSelectionnée, canImport } = manager
  const showUploadFilesModal = defineModel<boolean>()
  const dialog = useDialog()
  const fileInput = ref<HTMLInputElement | null>(null)
  const acceptedFileTypesString = ACCEPTED_FILE_TYPES.join(',')
  const importButtonIsDisabled = computed(() => !canImport.value)

  const selectFilesStart = () => fileInput.value?.click()
  const selectFilesEnd = (event: Event) => {
    const files = (event.target as HTMLInputElement).files
    if (files) manager.appendFiles(files)
  }

  const successHandler = () => {
    showUploadFilesModal.value = false
    dialog.showDialog({
      message: 'Vos fichiers apparaîtront dans la liste dans les 5 prochaines minutes.',
      type: 'Ok',
      title: 'Transmission réussie',
    })
  }

  const uploadFiles = async () => {
    try {
      await manager.uploadFiles()
      successHandler()
    } catch {}
  }
</script>

<style lang="less" scoped>
  .upload-item,
  .wrapperNatures,
  .natureLabel {
    margin-bottom: 12px;
  }

  .fileNames {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 2px;

    svg {
      margin-left: 6px;
      fill: #8993a8;
      transform: scale(0.6);
      cursor: pointer;
    }
  }
</style>
